import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import "./crearProyectoPublico.css";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

const estadoColor = (estado: string): "success" | "warning" | "error" | "default" => {
  const e = String(estado || "").toLowerCase();
  if (e.includes("rechaz") || e.includes("devuel")) return "error";
  if (e.includes("aprob")) return "success";
  if (e.includes("revision") || e.includes("pendiente")) return "warning";
  if (e.includes("completada")) return "success";
  if (e.includes("ejecucion")) return "warning";
  return "default";
};

const etiquetaEstadoEtapa = (etapa: { etapa_key?: string; estado?: string }, solicitudDecision?: string | null) => {
  const key = String(etapa?.etapa_key || "").toLowerCase();
  const decision = String(solicitudDecision || "").trim();
  if (key === "solicitud_servicio") {
    const d = decision.toLowerCase();
    if (d === "aprobado") return "Aprobada";
    if (d === "devuelto") return "Devuelta para corrección";
    if (d === "rechazado" || d === "rechazada") return "Rechazada";
    return decision || "En revisión";
  }
  return String(etapa?.estado || "Pendiente");
};

const unidadPorEtapa = (etapaKey: string) => {
  const key = String(etapaKey || "").toLowerCase().trim();
  if (key === "solicitud_servicio") return "Prospectiva";
  if (key === "factibilidad_servicio") return "Proyectos";
  if (key === "revision_aprobacion") return "Grandes Clientes y Proyectos";
  if (key === "ejecucion_obras") return "Mantenimiento Operacion y Proyectos";
  if (key === "puesta_servicio") return "Control de Energia";
  return "-";
};

const nombreEtapaMostrar = (etapaKey: string, etapaNombre: string) => {
  const key = String(etapaKey || "").toLowerCase().trim();
  if (key === "solicitud_servicio") return "Solicitud del servicio";
  if (key === "factibilidad_servicio") return "Diseño";
  if (key === "revision_aprobacion") return "Supervision y aprobacion";
  if (key === "ejecucion_obras") return "Ejecucion de obras";
  if (key === "puesta_servicio") return "Puesta en servicio";
  return etapaNombre || "-";
};

export default function EstadoProyectoPublicoPage() {
  const [codigo, setCodigo] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [detalle, setDetalle] = React.useState<any>(null);
  const backendBase = getProyectosApiBaseUrl();

  const fetchEstadoApi = async (path: string, init?: RequestInit) => {
    return fetch(`${backendBase}${path}`, init);
  };

  const consultarEstado = async () => {
    try {
      setLoading(true);
      setError(null);
      setDetalle(null);
      const res = await fetchEstadoApi(`/publico/estado`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible consultar el estado del proyecto.");
      setDetalle(data);
    } catch (e: any) {
      setError(e.message || "Error consultando estado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="tramites-public-page">
      <Box className="tramites-public-topbar">
        <Container maxWidth="lg" className="tramites-public-topbar-inner">
          <Box className="tramites-public-gov-wrap">
            <img src="/images/logo2.webp" alt="GOV.CO" className="tramites-public-gov-logo" />
          </Box>
          <Typography className="tramites-public-lang">ES</Typography>
        </Container>
      </Box>

      <Box className="tramites-public-logobar">
        <Container maxWidth="lg" className="tramites-public-logobar-inner">
          <Box className="tramites-public-logo-block">
            <img src="/images/logo-emcali.webp" alt="EMCALI" className="tramites-public-logo" />
          </Box>
          <a href="/proyectos/crear" className="tramites-public-back-btn">
            Volver
          </a>
        </Container>
      </Box>

      <Box className="tramites-public-main">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography className="tramites-public-title">Estado del proyecto</Typography>
          <Typography className="tramites-public-subtitle">
            Consulta el avance del trámite ingresando solo el código del proyecto.
          </Typography>
          <Paper className="tramites-public-card" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={1.5}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Código del proyecto" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
              <Button variant="contained" onClick={consultarEstado} disabled={loading}>
                Ver estado
              </Button>
            </Stack>
          </Paper>

          {detalle?.proyecto && (
            <Paper className="tramites-public-card" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {detalle.proyecto.nombre}
              </Typography>
              <Typography color="text.secondary">
                Código: {detalle.proyecto.codigo}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Estado global: {detalle.proyecto.estado_global || "-"}
              </Typography>

              <Alert severity="info" sx={{ mb: 1.2 }}>
                Etapa actual:{" "}
                <strong>
                  {nombreEtapaMostrar(
                    detalle?.etapa_actual?.etapa_key || "",
                    detalle?.etapa_actual?.etapa_nombre || "",
                  ) || "Sin etapa activa"}
                </strong>
                {" "}- Unidad:{" "}
                <strong>{unidadPorEtapa(detalle?.etapa_actual?.etapa_key || "")}</strong>
              </Alert>

              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.6 }}>
                <Typography variant="body2">Avance general</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {Number(detalle?.avance_general || 0)}%
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={Number(detalle?.avance_general || 0)} sx={{ mb: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Dashboard paso a paso
              </Typography>
              <Stack spacing={1}>
                {(detalle?.etapas || []).map((etapa: any) => (
                  <Paper key={etapa.etapa_key} variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {etapa.orden}. {nombreEtapaMostrar(etapa.etapa_key, etapa.etapa_nombre)}
                      </Typography>
                      <Chip
                        size="small"
                        label={etiquetaEstadoEtapa(etapa, detalle?.proyecto?.solicitud_decision)}
                        color={estadoColor(
                          etapa.etapa_key === "solicitud_servicio"
                            ? detalle?.proyecto?.solicitud_decision || etapa.estado
                            : etapa.estado,
                        )}
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Avance: {Number(etapa.porcentaje || 0)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Unidad: {unidadPorEtapa(etapa.etapa_key)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Última actualización: {etapa.updated_at ? new Date(etapa.updated_at).toLocaleString() : "-"}
                      </Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={Number(etapa.porcentaje || 0)} sx={{ mt: 0.8 }} />
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}
        </Container>
      </Box>
    </Box>
  );
}
